import { get } from 'sr/utils/axios/index'
import { getShowInternalAccounts } from 'sr/utils/helpers/internalAccounts'
import { JmmApiResponse, Status } from './globalInterface'

// New admin jobs interfaces
export interface JobDetails {
  _id: string
  id: string
  job_title: string
  companyName: string
  job_resource: 'internal' | 'external'
  createdAt: string
  applicationCount: number
  regionName?: string
  regionCity?: string
  regionState?: string
  regionId?: string
  additional_regions?: Array<{
    _id: string
    city?: string
    state?: string
  }>
  status: string
  company_id?: string
  external_company_name?: string
  isInternalCompany: boolean
  job_type?: string[]
  schedule?: string[]
  employment_type?: string[]
  show_pay_by?: string
  min_amount?: number
  max_amount?: number
  exact_amount?: number
  rate?: string
  start_date?: string
  deletedAt?: string
  isActive?: boolean
}

export interface JobStats {
  totalJobs: number
  activeJobs: number
  totalApplications: number
}

export interface FetchJobsResponse {
  success: boolean
  data: JobDetails[]
  pagination: {
    total: number
    page: number
    pageSize: number
    totalPages: number
    sort: Record<string, number>
    statusCounts?: any
    counts?: any
  }
  stats: JobStats
}

export interface JobFilters {
  page?: number
  limit?: number
  sort?: string
  includeInternal?: boolean
  job_resource?: string
  status?: string
  company_id?: string
  regionId?: string
  regionState?: string
  search?: string
  dateFrom?: string
  dateTo?: string
}

// Legacy interfaces (for company-specific job pages)
interface CompanyDetails {
  _id: string
  username: string
  email: string
  mobile_number: string
  company_name: string
  business_type: string[]
  intent: string[]
  candidate_msg: boolean
  user_id: string
  status: Status
  createdAt: string
  updatedAt: string
}

export interface JobAdvancedDetails {
  _id: string
  job_id: string
  reporting_address: {
    region?: string
    regionId?: {
      _id?: string
      city: string
      state: string
      coordinates: object
      center: object
      status: string
    }
    address_line_1: string
    address_line_2: string
    country: string
    city: string
    state: string
    postal: string
    lat: number
    lng: number
  },
  reporting_regions: any[],
  hire_count: number
  supplemental_pay: string[]
  recruitment_timeline: string
  application_deadline: string
  status: Status
  createdAt: string
  updatedAt: string
}

interface ApplicationDetails {
  _id: string
  job_id: string
  cleaner_id: string
  answers: string[]
  status: Status
  createdAt: string
  updatedAt: string
}

export interface JobResponse {
  company_id: CompanyDetails
  job_title: string
  job_type: string[]
  schedule: string[]
  employment_type: string[]
  start_date: string
  show_pay_by: string
  exact_amount: number
  min_amount?: number
  max_amount: number
  range: {
    min: number
    max: number
  }
  rate: string
  benefits: string[]
  job_description: string
  application_call_mobile_number: string
  require_resume: boolean
  notifications: boolean
  email: string
  application_ids: ApplicationDetails[]
  status: Status
  createdAt: string
  updatedAt: string
  job_advanced_id: JobAdvancedDetails
  application_status_counts: {
    hired: number
    active: number
  }
  id: string
  external_company_name?: string
  job_resource?: string
  supplemental_pay: string[]
}

const filterPayload = (payload: JobFilters) => {
  return Object.fromEntries(
    Object.entries(payload).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  )
}

// New admin jobs API
export const fetchJobs = async (payload: JobFilters): Promise<FetchJobsResponse> => {
  try {
    // Add includeInternal based on user preference if not explicitly set
    if (payload.includeInternal === undefined) {
      payload.includeInternal = getShowInternalAccounts()
    }

    const filteredPayload = filterPayload(payload)
    const res = await get<FetchJobsResponse>(`/admin/jobs`, filteredPayload)

    if (res.success === true && res.data) {
      return res
    } else {
      throw new Error('No data found')
    }
  } catch (error) {
    throw new Error(`Failed to fetch jobs: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

export const fetchJobStats = async (): Promise<{ success: boolean; data: any }> => {
  try {
    const includeInternal = getShowInternalAccounts()
    const res = await get<{ success: boolean; data: any }>(`/admin/jobs/stats`, { includeInternal })

    if (res.success === true && res.data) {
      return res
    } else {
      throw new Error('No data found')
    }
  } catch (error) {
    throw new Error(`Failed to fetch job stats: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

// Legacy function for fetching single job (company-specific pages)
export const fetchSingleJob = async (id: string): Promise<JobResponse> => {
  try {
    const res = await get<{ success: boolean; data: JobResponse | JobResponse[] }>(`/job`, { id, populate: 'company_id,job_advanced_id' })
    
    if (res.success === true && res.data) {
      // Backend may return a single object or an array depending on how aqp parses the id
      const job = Array.isArray(res.data) ? res.data[0] : res.data
      if (!job) {
        throw new Error('Job not found')
      }
      return job
    } else {
      throw new Error('No data found')
    }
  } catch (error) {
    throw new Error(`Failed to fetch job: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}
